import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function AdminLogin() {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) navigate('/admin', { replace: true });
  }, [isAuthenticated, navigate]);

  const returnToPath =
    (location.state?.from?.pathname || '/admin') +
    (location.state?.from?.search || '');

  const styles = {
    page: {
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: '32px',
      // layered gradient with subtle brand glow
      background:
        'radial-gradient(1200px 600px at 10% 0%, rgba(217,60,61,0.20) 0%, rgba(217,60,61,0.00) 60%),' +
        'radial-gradient(900px 500px at 90% 10%, rgba(59,89,152,0.18) 0%, rgba(59,89,152,0.00) 60%),' +
        'linear-gradient(135deg, #0f1724 0%, #171c2a 100%)',
      color: '#f7f7f9',
    },
    cardWrap: {
      width: '100%',
      maxWidth: 920,
    },
    card: {
      border: 'none',
      borderRadius: 18,
      overflow: 'hidden',
      background: 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(8px)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    },
    header: {
      background:
        'linear-gradient(135deg, #D93C3D 0%, #AF2F2F 60%, #9a2a2a 100%)',
      padding: '28px 28px',
      color: 'white',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      background: 'rgba(255,255,255,0.15)',
      padding: '6px 12px',
      borderRadius: 999,
      fontSize: 12,
      letterSpacing: 0.4,
    },
    title: {
      margin: '12px 0 4px 0',
      fontWeight: 800,
      fontSize: 28,
      lineHeight: 1.2,
      letterSpacing: 0.2,
    },
    subtitle: {
      margin: 0,
      opacity: 0.95,
      fontSize: 14,
    },
    body: {
      padding: 28,
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1.2fr 0.8fr',
      gap: 24,
    },
    gridNarrow: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 16,
    },
    section: {
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 14,
      padding: 18,
    },
    sectionTitle: {
      margin: '0 0 10px 0',
      fontSize: 16,
      fontWeight: 700,
      color: '#eaeaf2',
    },
    list: {
      margin: 0,
      paddingLeft: 18,
      color: '#d9d9e3',
      lineHeight: 1.7,
      fontSize: 14,
    },
    ctaWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      justifyContent: 'center',
      alignItems: 'stretch',
      height: '100%',
    },
    loginBtn: {
      border: 'none',
      borderRadius: 14,
      padding: '14px 18px',
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: 0.3,
      color: '#fff',
      cursor: 'pointer',
      background:
        'linear-gradient(90deg, #D93C3D 0%, #AF2F2F 60%, #8f2424 100%)',
      boxShadow:
        '0 10px 24px rgba(175,47,47,0.45), inset 0 -2px 0 rgba(0,0,0,0.2)',
      transition: 'transform 120ms ease, box-shadow 120ms ease',
    },
    loginBtnHover: {
      transform: 'translateY(-1px)',
      boxShadow:
        '0 14px 26px rgba(175,47,47,0.55), inset 0 -2px 0 rgba(0,0,0,0.22)',
    },
    ghostBtn: {
      borderRadius: 14,
      padding: '12px 16px',
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: 0.2,
      color: '#fff',
      background: 'transparent',
      border: '1.5px solid rgba(255,255,255,0.35)',
      backdropFilter: 'blur(4px)',
    },
    footer: {
      padding: '14px 24px 22px',
      color: '#cfcfe6',
      fontSize: 12,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTop: '1px solid rgba(255,255,255,0.12)',
    },
    brandDot: {
      width: 10,
      height: 10,
      borderRadius: 999,
      background: '#3B5998',
      boxShadow: '0 0 0 4px rgba(59,89,152,0.18)',
      marginRight: 8,
    },
    featuresRow: {
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      marginTop: 10,
    },
    featureChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 10px',
      borderRadius: 999,
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.12)',
      fontSize: 12,
    },
    // simple hover helper for inline styles
    hoverable: { cursor: 'pointer' },
  };

  // small inline hover trick
  const onMouse = (e, hoverStyle) => {
    Object.assign(e.currentTarget.style, hoverStyle);
  };
  const onMouseLeave = (e, baseStyle) => {
    Object.assign(e.currentTarget.style, baseStyle);
  };

  return (
    <div style={styles.page}>
      <div style={styles.cardWrap}>
        <div style={styles.card}>

          {/* HEADER */}
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={styles.badge}>
                <i className="lni lni-shield"></i> Admin Panel
              </span>
            </div>
            <h1 style={styles.title}>
              Komponistinnen Musikfestival
            </h1>
            <p style={styles.subtitle}>
              Willkommen im Verwaltungsbereich – Inhalte sicher, schnell und stilvoll pflegen.
            </p>
            <div style={styles.featuresRow}>
              <span style={styles.featureChip}><i className="lni lni-lock"></i> Geschützter Zugang</span>
              <span style={styles.featureChip}><i className="lni lni-music"></i> Programm verwalten</span>
              <span style={styles.featureChip}><i className="lni lni-image"></i> Galerie aktualisieren</span>
            </div>
          </div>

          {/* BODY */}
          <div style={styles.body}>
            {/* responsive: 2 cols on wide, 1 col on narrow */}
            <div className="d-none d-md-block" style={styles.grid}>
              <LeftInfo styles={styles} />
              <RightCTA
                styles={styles}
                onLogin={() =>
                  loginWithRedirect({ appState: { returnTo: returnToPath } })
                }
                onMouse={onMouse}
                onMouseLeave={onMouseLeave}
              />
            </div>

            <div className="d-block d-md-none" style={styles.gridNarrow}>
              <LeftInfo styles={styles} />
              <RightCTA
                styles={styles}
                onLogin={() =>
                  loginWithRedirect({ appState: { returnTo: returnToPath } })
                }
                onMouse={onMouse}
                onMouseLeave={onMouseLeave}
              />
            </div>
          </div>

          {/* FOOTER */}
          <div style={styles.footer}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={styles.brandDot} />
              <span>Komponistinnen Musikfestival • Admin</span>
            </div>
            <div style={{ opacity: 0.85 }}>
              © {new Date().getFullYear()} – Frankfurt am Main
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----- sub-components (kept inline for drop-in simplicity) ----- */

function LeftInfo({ styles }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>
        <i className="lni lni-bulb" /> Was kann ich hier tun?
      </h3>
      <ul style={styles.list}>
        <li>Allgemeine Texte (Hero, Einleitung, Veranstaltungsort) aktualisieren</li>
        <li>Konzerte & Programminfos pflegen</li>
        <li>Künstler*innen-Seite bearbeiten</li>
        <li>Festival-Galerie verwalten</li>
        <li>Kontakt- & Ticket-Infos anpassen</li>
      </ul>
      <div className="mt-3" style={{ color: '#cfd7ff', fontSize: 13 }}>
        Hinweis: Änderungen sind sofort nach dem Speichern auf der Website sichtbar.
      </div>
    </div>
  );
}

function RightCTA({ styles, onLogin, onMouse, onMouseLeave }) {
  return (
    <div style={styles.ctaWrap}>
      <button
        type="button"
        style={styles.loginBtn}
        onMouseEnter={(e) => onMouse(e, styles.loginBtnHover)}
        onMouseLeave={(e) => onMouseLeave(e, styles.loginBtn)}
        onClick={onLogin}
      >
        <i className="lni lni-unlock" style={{ marginRight: 10 }} />
        Anmelden &rarr; Admin öffnen
      </button>

      <a
        href="/"
        className="text-decoration-none text-white"
        style={styles.ghostBtn}
        title="Zur öffentlichen Website"
      >
        <i className="lni lni-home" style={{ marginRight: 8 }} />
        Zur Website
      </a>

      <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
        Mit Klick auf „Anmelden“ stimmen Sie der sicheren Anmeldung via Auth0 zu.
      </div>
    </div>
  );
}
